import type { DiveObject, SpotDetail } from "@divespots/contracts";
import { pickLocale } from "@divespots/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { Button } from "../components/Button.tsx";
import { List } from "../components/List.tsx";
import { ObjectIcon } from "../components/ObjectIcon.tsx";
import { formatResolvedDepth } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { ObjectFields } from "./ObjectFields.tsx";
import { ObjectModelSection } from "./models/ObjectModelSection.tsx";
import {
  emptyObjectForm,
  isFormDirty,
  issuesToErrors,
  objectToForm,
  validateObjectCreate,
  validateObjectPatch,
} from "./forms.ts";
import type { FieldErrors, Issue, ObjectForm } from "./forms.ts";
import { DiscardConfirm, DiscardSheet, useUnsavedGuard } from "./unsaved.tsx";
import { ConfirmSheet, Section } from "./ui.tsx";

/** Objects of one spot: list, create, edit, delete. Depth is computed server-side from the lakebed. */
export function ObjectsSection({ spot }: { spot: SpotDetail }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ object: DiveObject | null } | null>(null);
  const [deleting, setDeleting] = useState<DiveObject | null>(null);
  // Switching to another object (or closing the form) while the open one has unsaved edits asks first.
  const formDirty = useRef(false);
  const modelDirty = useRef(false);
  const [pending, setPending] = useState<{ object: DiveObject | null } | "close" | null>(null);
  const onModelDirty = useCallback((d: boolean) => {
    modelDirty.current = d;
  }, []);
  // true when only the staged 3D model is unsaved (the object form itself is saved or unchanged)
  const [modelOnly, setModelOnly] = useState(false);
  const request = (target: { object: DiveObject | null } | null) => {
    if (formDirty.current || modelDirty.current) {
      setModelOnly(!formDirty.current);
      return setPending(target ?? "close");
    }
    setEditing(target);
  };

  const q = useQuery({
    queryKey: ["admin", "objects", spot.id],
    queryFn: ({ signal }) => api.listObjects({ spotId: spot.id }, signal).then((r) => r.objects),
  });

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["admin"] });
    await qc.invalidateQueries({ queryKey: ["spot"] });
    await qc.invalidateQueries({ queryKey: ["spots"] });
  };

  const del = useMutation({
    mutationFn: (o: DiveObject) => api.deleteObject(o.id),
    onSuccess: async () => {
      setDeleting(null);
      await refresh();
    },
  });

  const objects = q.data ?? [];

  return (
    <Section
      title={t("admin.objects.title")}
      actions={
        <Button
          variant="filled-lake"
          icon={<Plus aria-hidden="true" strokeWidth={1.75} />}
          onClick={() => request({ object: null })}
        >
          {t("admin.objects.add")}
        </Button>
      }
    >
      {q.isPending ? <p className="m-0">{t("common.loading")}</p> : null}
      {q.isError ? (
        <p className="adm-error m-0" role="alert">
          {t("admin.err.load")}
        </p>
      ) : null}
      {q.isSuccess && objects.length === 0 && !editing ? (
        <p className="ds-text-subheadline text-label-secondary m-0">{t("admin.objects.none")}</p>
      ) : null}

      {objects.length > 0 ? (
        <List label={t("admin.objects.title")}>
          {objects.map((o) => (
            <li key={o.id} className="ds-row adm-object-row">
              <span className="ds-row__icon bg-lake-tint text-lake-on-tint" aria-hidden="true">
                <ObjectIcon type={o.type} />
              </span>
              <span className="ds-row__title">
                {pickLocale(o.name, locale) ?? t(`objects.type.${o.type}`)}
                {o.status !== "published" ? (
                  <span className="ds-text-footnote text-label-secondary">
                    {" · "}
                    {t(`admin.status.${o.status}`)}
                  </span>
                ) : null}
              </span>
              <span className="ds-row__value ds-row__value--depth">
                {formatResolvedDepth(o.depth, locale) ?? t("objects.noData")}
              </span>
              <Button
                variant="plain"
                aria-label={t("admin.objects.edit", { name: pickLocale(o.name, locale) ?? "" })}
                icon={<Pencil aria-hidden="true" strokeWidth={1.75} />}
                onClick={() => request({ object: o })}
              />
              <Button
                variant="destructive"
                aria-label={t("admin.objects.delete", { name: pickLocale(o.name, locale) ?? "" })}
                icon={<Trash2 aria-hidden="true" strokeWidth={1.75} />}
                onClick={() => setDeleting(o)}
              />
            </li>
          ))}
        </List>
      ) : null}

      {editing ? (
        <ObjectForm
          key={editing.object?.id ?? "new"}
          spot={spot}
          object={editing.object}
          onClose={() => request(null)}
          onDirtyChange={(d) => {
            formDirty.current = d;
          }}
          onSaved={async () => {
            formDirty.current = false;
            await refresh();
            // staged model edits are not part of the object form: ask before they go
            if (modelDirty.current) {
              setModelOnly(true);
              setPending("close");
            } else setEditing(null);
          }}
        />
      ) : null}
      {editing?.object ? (
        <ObjectModelSection
          key={`model-${editing.object.id}`}
          object={editing.object}
          onDirtyChange={onModelDirty}
        />
      ) : null}

      {pending ? (
        <DiscardConfirm
          body={modelOnly ? t("admin.model.discardBody") : undefined}
          onDiscard={() => {
            formDirty.current = false;
            modelDirty.current = false;
            setEditing(pending === "close" ? null : pending);
            setPending(null);
          }}
          onKeep={() => setPending(null)}
        />
      ) : null}

      {deleting ? (
        <ConfirmSheet
          title={t("admin.objects.deleteTitle")}
          body={t("admin.objects.deleteBody", { name: pickLocale(deleting.name, locale) ?? "" })}
          confirmLabel={t("admin.common.delete")}
          busy={del.isPending}
          error={del.isError ? t("admin.err.save") : undefined}
          onConfirm={() => del.mutate(deleting)}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </Section>
  );
}

/**
 * The staff object form (also used by the 3D view's "place object"): `initial` pre-fills a new
 * object (e.g. the tapped location) and `notice` is shown above the fields.
 */
export function ObjectForm({
  spot,
  object,
  initial,
  notice,
  picker3d = true,
  onClose,
  onDirtyChange,
  onSaved,
}: {
  spot: SpotDetail;
  object: DiveObject | null;
  initial?: ObjectForm | undefined;
  notice?: ReactNode;
  /** Offer the embedded 3D picker (not inside the 3D view itself). Default true. */
  picker3d?: boolean;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const start = () => (object ? objectToForm(object) : (initial ?? emptyObjectForm(spot.location)));
  const [form, setForm] = useState<ObjectForm>(start);
  const baseline = useMemo(start, [object, initial, spot.location]);
  const dirty = isFormDirty(form, baseline);
  const { blocker } = useUnsavedGuard(dirty);
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [locale, setLocale] = useState<"de" | "en" | "fr" | "it">("de");

  const save = useMutation({
    mutationFn: async () => {
      const v = object
        ? validateObjectPatch(object, form, t)
        : validateObjectCreate(spot.id, form, t);
      if (!v.ok) throw new FormInvalid(v.errors);
      if (object) {
        if (Object.keys(v.data).length === 0) return;
        await api.updateObject(object.id, v.data);
      } else {
        await api.createObject(v.data as never);
      }
    },
    onSuccess: onSaved,
    onError: (e) => {
      setBanner(null);
      if (e instanceof FormInvalid) return setErrors(e.errors);
      if (isApiRequestError(e) && e.code === "bad_request" && Array.isArray(e.details)) {
        return setErrors(issuesToErrors(e.details as Issue[], t, true));
      }
      setBanner(t("admin.err.save"));
    },
  });

  const set = <K extends keyof ObjectForm>(k: K, v: ObjectForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  return (
    <form
      className="adm-subform"
      aria-label={object ? t("admin.objects.editTitle") : t("admin.objects.addTitle")}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setErrors({});
        setBanner(null);
        save.mutate();
      }}
    >
      <h3 className="ds-text-title-3 m-0">
        {object ? t("admin.objects.editTitle") : t("admin.objects.addTitle")}
      </h3>

      {notice}

      <ObjectFields
        mode="staff"
        form={form}
        errors={errors}
        set={set}
        setForm={setForm}
        setErrors={setErrors}
        locale={locale}
        setLocale={setLocale}
        spotLocation={spot.location}
        spot={picker3d ? spot : undefined}
      />

      {banner ? (
        <p className="adm-error m-0" role="alert">
          {banner}
        </p>
      ) : null}
      <div className="adm-actions">
        <Button type="submit" variant="filled-lake" disabled={save.isPending}>
          {t("admin.common.save")}
        </Button>
        <Button variant="tinted" onClick={onClose}>
          {t("admin.common.cancel")}
        </Button>
      </div>
      <DiscardSheet blocker={blocker} />
    </form>
  );
}

class FormInvalid extends Error {
  constructor(readonly errors: FieldErrors) {
    super("invalid");
  }
}
